/**
 * A seção "Outras evidências" da ficha da visita (ADR-048; spec evidencias-complementares, Req 6.1, design §5): as
 * evidências complementares ligadas à visita — o tipo, a data, o que foi lido, o arquivo e como foi ligada. Clicar numa
 * evidência a abre no visualizador da ficha (que alterna "Termo"/"Evidência"); a aberta fica marcada. Só o que a API
 * traz; na tela, nunca o código interno (a classe, o critério, a chave do campo).
 */

import { rotuloTipoDocumento } from './format';
import { rotuloCampoEvidencia } from '../services/rotulos-campos';
import { CriterioLigacao, EvidenciaComplementar } from '../services/consulta-types';

/** Como a evidência foi ligada à visita (design §3), em palavras. */
export const ROTULO_CRITERIO: Readonly<Record<CriterioLigacao, string>> = {
  proprio: 'Impressa no próprio termo',
  inep_caminho: 'Ligada pelo INEP no nome do arquivo ou da pasta',
  pasta: 'Ligada pela pasta do termo',
  inep_lido: 'Ligada pelo INEP lido na evidência',
  citada: 'A escola é citada no documento',
};

/** O título de uma evidência: o tipo e a data ("sem data" quando não foi lida). */
export function tituloDaEvidencia(e: EvidenciaComplementar): string {
  return `${rotuloTipoDocumento(e.tipo)} · ${e.dataHora ?? 'sem data'}`;
}

/** O arquivo da evidência, com a página quando é de um PDF/TIFF. */
export function arquivoDaEvidencia(e: EvidenciaComplementar): string {
  return typeof e.pagina === 'number' ? `${e.caminhoOriginal} — página ${e.pagina}` : e.caminhoOriginal;
}

export function OutrasEvidenciasSecao({
  evidencias,
  aberta,
  onAbrir,
}: {
  readonly evidencias: readonly EvidenciaComplementar[];
  /** A evidência que está no visualizador agora (null = o termo). */
  readonly aberta: string | null;
  readonly onAbrir: (evidenciaId: string) => void;
}): JSX.Element {
  return (
    <section className="card" aria-label="Outras evidências" data-testid="outras-evidencias">
      <h2>Outras evidências</h2>
      {evidencias.length === 0 ? (
        <p className="muted">Nenhuma outra evidência ligada a esta visita.</p>
      ) : (
        <>
          <p className="muted">Clique numa evidência para vê-la no visualizador do documento.</p>
          <ul className="lista-evidencias" aria-label="Evidências da visita">
            {evidencias.map((e) => {
              const ativa = e.id === aberta;
              return (
                <li key={e.id} data-testid={`evidencia-${e.id}`}>
                  <button
                    type="button"
                    className={ativa ? 'btn btn-primary' : 'btn btn-ghost'}
                    aria-pressed={ativa}
                    onClick={() => onAbrir(e.id)}
                  >
                    {tituloDaEvidencia(e)}
                  </button>
                  {e.resumo.length > 0 && (
                    <dl className="resumo-evidencia">
                      {e.resumo.map((r, i) => (
                        <div key={`${r.campo}-${i}`}>
                          <dt>{rotuloCampoEvidencia(r.campo)}</dt>
                          <dd>{r.valor}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  <p className="muted">
                    <span className="mono">{e.caminhoOriginal}</span>
                    {typeof e.pagina === 'number' && ` — página ${e.pagina}`}
                    {` · ${ROTULO_CRITERIO[e.criterio] ?? 'Ligada à visita'}`}
                  </p>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
